import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Warning, ArrowSquareOut, Receipt, HandCoins } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * UTR Trail Modal
 *
 * Shows every past deposit + withdraw entry that used the same UTR — with
 * a "Jump to chat" button so the handler can verify context (fraud vs.
 * accidental double-entry) in one click.
 *
 * Props:
 *   - utr: string (required)
 *   - open: bool
 *   - onClose: () => void
 *   - actorRole?: 'super_admin' | 'deposit_handler' | 'withdrawal_handler' | ...
 *   - initialMatches?: array — if you already have the /utr/check response,
 *     pass it so we skip the second fetch.
 */
const UtrTrailModal = ({ utr, open, onClose, actorRole, initialMatches }) => {
  const [matches, setMatches] = useState(Array.isArray(initialMatches) ? initialMatches : []);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !utr) return;
    // Always re-fetch so we get the enriched room_id fields even if the
    // parent passed a slimmer initialMatches list.
    let stopped = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/utr/check`, { params: { utr } });
        if (!stopped) setMatches(data.matches || []);
      } catch (e) {
        if (!stopped) toast.error('Failed to load UTR trail');
      } finally {
        if (!stopped) setLoading(false);
      }
    })();
    return () => { stopped = true; };
  }, [open, utr]);

  if (!open) return null;

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  const jumpToChat = (m) => {
    if (!m.room_id) { toast.info('No active chat room for this entry'); return; }
    const path = actorRole === 'super_admin' ? `/admin-chat/${m.room_id}` : `/handler-chat/${m.room_id}`;
    onClose?.();
    navigate(path);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-3"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="utr-trail-modal"
    >
      <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden shadow-2xl max-h-[85vh] flex flex-col">
        <div className="px-4 py-3 bg-red-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Warning size={20} weight="fill" />
            <div className="min-w-0">
              <div className="font-bold text-sm">Duplicate UTR Trail</div>
              <div className="text-[11px] opacity-90 truncate font-mono">{utr}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full"
            data-testid="utr-trail-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-2 overflow-y-auto flex-1">
          {loading && matches.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-8">Loading trail…</div>
          )}
          {!loading && matches.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-8">No prior entries for this UTR.</div>
          )}
          {matches.map((m, idx) => {
            const isDep = m.type === 'deposit';
            const Icon = isDep ? Receipt : HandCoins;
            const accent = isDep ? 'bg-green-100 text-green-700 border-green-200' : 'bg-orange-100 text-orange-700 border-orange-200';
            return (
              <div
                key={m.entry_id || idx}
                className={`border rounded-lg p-3 ${accent}`}
                data-testid={`utr-trail-row-${idx}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5 font-bold text-xs uppercase tracking-wide">
                    <Icon size={14} weight="fill" />
                    <span>{isDep ? 'Deposit' : 'Withdraw'}</span>
                    {m.status && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-white/60">{m.status}</span>}
                  </div>
                  <button
                    onClick={() => jumpToChat(m)}
                    disabled={!m.room_id}
                    className="text-[11px] flex items-center gap-1 px-2 py-1 rounded bg-white hover:bg-blue-50 border border-gray-200 text-blue-700 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`utr-trail-jump-${idx}`}
                  >
                    <ArrowSquareOut size={12} weight="bold" /> Jump to chat
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-gray-800">
                  <div><span className="text-gray-500">Client:</span> <b>{m.customer_username || '-'}</b></div>
                  <div><span className="text-gray-500">Amount:</span> <b>₹{Number(m.amount || 0).toLocaleString('en-IN')}</b></div>
                  <div className="col-span-2"><span className="text-gray-500">When:</span> {fmtDate(m.created_at)}</div>
                  {isDep && m.received_in && (
                    <div className="col-span-2 truncate"><span className="text-gray-500">Received In:</span> {m.received_in}</div>
                  )}
                  {isDep && m.received_from && (
                    <div className="col-span-2 truncate"><span className="text-gray-500">From:</span> {m.received_from}</div>
                  )}
                  {!isDep && m.paid_from_bank && (
                    <div className="col-span-2 truncate"><span className="text-gray-500">Paid From:</span> {m.paid_from_bank}</div>
                  )}
                  {isDep && m.description && (
                    <div className="col-span-2 truncate"><span className="text-gray-500">Note:</span> {m.description}</div>
                  )}
                  {m.actor_display && (
                    <div className="col-span-2"><span className="text-gray-500">By:</span> {m.actor_display}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2.5 border-t bg-gray-50 flex items-center justify-between text-[11px] text-gray-500">
          <span>Total matches: <b className="text-gray-700">{matches.length}</b></span>
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-xs font-semibold text-gray-700"
            data-testid="utr-trail-dismiss-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UtrTrailModal;
